import React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useInfiniteProjectsAnalyticsProjectsReportsSignatureStatusReportsDashboardReportQuery as useInfiniteSignatureStatusReportsDashboardReportQuery } from 'components/hooks/projectsAnalyticsProjectsReportsSignatureStatusReportsDashboardReportQueries';
import { useProjectsAnalyticsSignatureStatusReportQuery as useSignatureStatusReportQuery } from 'components/hooks/projectsAnalyticsSignatureStatusReportQueries';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import Card from 'components/projectsAnalytics/components/Card';
import NoDataMessage from 'components/projectsAnalytics/components/NoDataMessage';
import ProjectsModal from 'components/projectsAnalytics/components/ProjectsModal';
import ReportHeader from 'components/projectsAnalytics/components/ReportHeader';
import StatusReportBody from 'components/projectsAnalytics/components/StatusReportBody';
import {
  filtersFormatter,
  filtersListFormatter,
} from 'components/projectsAnalytics/helpers/reportHeader';
import useFilters from 'components/projectsAnalytics/hooks/useFilters';
import useProjectsModal from 'components/projectsAnalytics/hooks/useProjectsModal';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

const staticChartData = [
  {
    cellClassName: 'tw-fill-green-400',
    countKey: 'fullySignedSignaturesCount',
    dotClassName: 'tw-bg-green-400',
    filterName: 'fully_signed',
    tKey: 'fullySigned',
  },
  {
    cellClassName: 'tw-fill-amber-300',
    countKey: 'pendingSignaturesCount',
    dotClassName: 'tw-bg-amber-300',
    filterName: 'pending_signatures',
    tKey: 'pendingSignatures',
  },
  {
    cellClassName: 'tw-fill-red-400',
    countKey: 'hasRejectionsSignaturesCount',
    dotClassName: 'tw-bg-red-400',
    filterName: 'has_rejections',
    tKey: 'hasRejections',
  },
  {
    cellClassName: 'tw-fill-mutedBlue-300',
    countKey: 'notRequestedSignaturesCount',
    dotClassName: 'tw-bg-mutedBlue-300',
    filterName: 'not_requested',
    tKey: 'notRequested',
  },
];

export default function SignatureStatusCard({
  dashboardFilterParams,
  isGeneratingExport,
  onExportClick,
}) {
  const currentActor = useCurrentActor();

  const { filters: cardFilters, handleFilterChange: handleCardFilterChange } =
    useFilters({ live: true, future: true });

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const {
    domainDashboardConfigurableFields,
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isFoldersEnabled,
    isProjectReviewDatesEnabled,
    isRiskRegisterEnabled,
  } = projectsAnalyticsDashboardContext;

  const { closeProjectsModal, openProjectsModal, projectsModal } =
    useProjectsModal({
      domainDashboardConfigurableFields,
      isApprovalsEnabled,
      isDigitalSignaturesEnabled,
      isFoldersEnabled,
      isProjectReviewDatesEnabled,
      isRiskRegisterEnabled,
    });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: signatureStatusReport } = useSignatureStatusReportQuery({
    params: { filter: { ...cardFilters, ...dashboardFilterParams } },
    queryOptions: { keepPreviousData: true },
  });

  const {
    data: domainDashboardReport,
    fetchNextPage: fetchDomainDashboardReportNextPage,
    hasNextPage: hasDomainDashboardReportNextPage,
  } = useInfiniteSignatureStatusReportsDashboardReportQuery({
    params: {
      filter: projectsModal.filters,
      selections: projectsModal.selections,
    },
    queryOptions: { enabled: projectsModal.isOpen },
  });

  const handleExportClick = () => {
    onExportClick({ params: { filter: projectsModal.filters } });
  };

  const handleSegmentClick = ({ entry }) => {
    openProjectsModal({
      filters: {
        lifecycle_status: {
          in: _([])
            .tap((lifecycleStatuses) => {
              if (cardFilters.live) lifecycleStatuses.push('live');
              if (cardFilters.future) lifecycleStatuses.push('future');
            })
            .value(),
        },
        signature_status: { eq: entry.filterName },
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle: pluralizeProjectsName({
        count: entry.count,
        currentActor,
        shouldIncludeCount: true,
      }),
      title: `${i18nTCommon('projects.signatureStatus')}: ${entry.name}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Signature status',
        filters: cardFilters,
        segment: entry.name,
      },
    );
  };

  const handleProjectClick = (project) => {
    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: project click',
      {
        chart: 'Signature status',
        filters: cardFilters,
        project: project.attributes.name,
      },
    );
  };

  const {
    fullySignedSignaturesCount,
    futureFilterCount,
    hasRejectionsSignaturesCount,
    liveFilterCount,
    notRequestedSignaturesCount,
    pendingSignaturesCount,
  } = signatureStatusReport?.data.attributes || {};

  const totalCount = Object.values({
    fullySignedSignaturesCount,
    pendingSignaturesCount,
    hasRejectionsSignaturesCount,
    notRequestedSignaturesCount,
  }).reduce((a, b) => a + b, 0);

  const filterCounts = { live: liveFilterCount, future: futureFilterCount };
  const filterDots = { live: 'green', future: 'amber' };

  const dynamicChartData = [
    fullySignedSignaturesCount,
    pendingSignaturesCount,
    hasRejectionsSignaturesCount,
    notRequestedSignaturesCount,
  ];

  const chartData = staticChartData.map((data, index) => ({
    ...data,
    count: dynamicChartData[index],
    name: i18nTCommon(`projects.${data.tKey}`),
  }));

  const filterTranslations = {
    future: i18nTCommon('shared.future'),
    live: i18nTCommon('shared.live'),
  };

  return (
    <Card>
      <ReportHeader
        filters={filtersFormatter({
          filterCounts,
          filterDots,
          filters: cardFilters,
          translations: filterTranslations,
        })}
        minChecked={1}
        onFilterChange={handleCardFilterChange}
        subtitle={filtersListFormatter({
          filters: cardFilters,
          pluralRAMSName: currentActor.division.attributes.pluralRAMSName,
          translations: filterTranslations,
        })}
        title={i18nTCommon('projects.signatureStatus')}
      />
      {signatureStatusReport &&
        (totalCount > 0 ?
          <StatusReportBody
            chartData={chartData}
            onSegmentClick={handleSegmentClick}
            totalCount={totalCount}
          />
        : <NoDataMessage
            message={i18nTCommon('shared.checkAppliedFilters')}
          />)}
      {projectsModal.isOpen && (
        <ProjectsModal
          domainDashboardReport={domainDashboardReport}
          hasNextPage={hasDomainDashboardReportNextPage}
          isGeneratingExport={isGeneratingExport}
          onClose={closeProjectsModal}
          onExportClick={handleExportClick}
          onFetchNextPage={() => {
            fetchDomainDashboardReportNextPage();
            handleFetchNextPageOfProjects();
          }}
          onProjectClick={handleProjectClick}
          selections={projectsModal.selections}
          subtitle={projectsModal.subtitle}
          title={projectsModal.title}
        />
      )}
    </Card>
  );
}

SignatureStatusCard.propTypes = {
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isGeneratingExport: PropTypes.bool.isRequired,
  onExportClick: PropTypes.func.isRequired,
};
